import React, { useEffect, useState, useRef, createElement, Component } from 'react';
import { BuildingIcon, PlusIcon, SearchIcon, MapPinIcon, PhoneIcon, UserIcon, EditIcon, TrashIcon, NavigationIcon, CheckCircleIcon, XCircleIcon, MapIcon } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Badge } from '../components/ui/Badge';
import { PageHeader } from '../components/ui/PageHeader';
import { Modal, ModalBody, ModalFooter } from '../components/ui/Modal';
import { Pagination } from '../components/ui/Pagination';
import { useAuth } from '../context/AuthContext';
import { useApp } from '../context/AppContext';
import { Client } from '../types';
import L from 'leaflet';
import { LeafletMap } from '../components/ui/LeafletMap';
import { hasGoogleApiKey, searchGooglePlaces } from '../lib/googleMaps';
export function ClientsPage() {
  const {
    user,
    addActivityLog
  } = useAuth();
  const {
    clients,
    users,
    addClient,
    updateClient,
    deleteClient,
    getCurrentLocation
  } = useApp();
  const isAdmin = user?.rolNombre === 'Administrador' || user?.permisos?.users === true;
  const [searchQuery, setSearchQuery] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [activeTab, setActiveTab] = useState<'info' | 'location'>('info');
  const [isSaving, setIsSaving] = useState(false);
  // Address search state
  const [addressSearch, setAddressSearch] = useState('');
  const [searchResults, setSearchResults] = useState<Array<{
    display_name: string;
    lat: string;
    lon: string;
  }>>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [locationError, setLocationError] = useState('');
  const [isGettingLocation, setIsGettingLocation] = useState(false);
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Form state
  const [formData, setFormData] = useState({
    codigo: '',
    nombre: '',
    telefono: '',
    ciudad: '',
    limite: '',
    cedula: '',
    rnc: '',
    ruta: '',
    direccion: '',
    vendedorId: '',
    vendedorNombre: '',
    dependencia: '',
    zona: '',
    lat: null as number | null,
    lng: null as number | null
  });
  // Map refs
  const mapInstanceRef = useRef<L.Map | null>(null);
  const markerRef = useRef<L.Marker | null>(null);
  const userLocationMarkerRef = useRef<L.Marker | null>(null);
  const mapDestroyedRef = useRef(false);
  const vendors = users.filter(u => u.rolNombre !== 'Administrador' && u.activo && u.permisos?.['new-visit'] === true);
  const vendorOptions = [{
    value: '',
    label: 'Sin vendedor asignado'
  }, ...vendors.map(v => ({
    value: v.id,
    label: v.nombre
  }))];
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 8;
  const filteredClients = clients.filter(c => {
    const query = searchQuery.toLowerCase();
    return c.nombre.toLowerCase().includes(query) || c.zona.toLowerCase().includes(query) || c.codigo && c.codigo.toLowerCase().includes(query);
  });
  const totalPages = Math.ceil(filteredClients.length / ITEMS_PER_PAGE);
  const paginatedClients = filteredClients.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE);
  const handleMapReady = (map: L.Map) => {
    mapInstanceRef.current = map;
    mapDestroyedRef.current = false;
    // Setup map interactions
    map.on('click', (e: L.LeafletMouseEvent) => {
      if (mapDestroyedRef.current) return;
      const {
        lat,
        lng
      } = e.latlng;
      setFormData(prev => ({
        ...prev,
        lat,
        lng
      }));
      updateMarker(lat, lng);
    });
    // Initial marker if data exists
    if (formData.lat && formData.lng) {
      updateMarker(formData.lat, formData.lng);
      map.setView([formData.lat, formData.lng], 15);
    }
    // Show user location if available
    getCurrentLocation().then(loc => {
      if (!loc || mapDestroyedRef.current) return;
      const userIcon = L.divIcon({
        html: `
          <div style="position:relative;width:20px;height:20px;">
            <div style="position:absolute;inset:0;background:#3b82f6;border-radius:50%;opacity:0.25;animation:ping 1.5s cubic-bezier(0,0,0.2,1) infinite;"></div>
            <div style="position:absolute;inset:3px;background:#3b82f6;border-radius:50%;border:2px solid white;box-shadow:0 1px 4px rgba(0,0,0,0.3);"></div>
          </div>
        `,
        className: '',
        iconSize: [20, 20],
        iconAnchor: [10, 10]
      });
      const userMarker = L.marker([loc.lat, loc.lng], {
        icon: userIcon
      }).addTo(map).bindPopup('<div style="font-family:system-ui;font-size:13px;font-weight:700;color:#1e40af;">📍 Tu ubicación actual</div>', {
        maxWidth: 180
      });
      userLocationMarkerRef.current = userMarker;
      // If no client location set yet, center on user
      if (!formData.lat && !formData.lng) {
        map.setView([loc.lat, loc.lng], 15);
      }
    });
  };
  const updateMarker = (lat: number, lng: number) => {
    if (!mapInstanceRef.current) return;
    // Custom red teardrop SVG icon — avoids broken default Leaflet icon
    const pinIcon = L.divIcon({
      html: `
        <div style="position:relative;width:30px;height:40px;">
          <svg viewBox="0 0 24 32" width="30" height="40" xmlns="http://www.w3.org/2000/svg"
            style="filter:drop-shadow(0 2px 4px rgba(0,0,0,0.35));display:block;">
            <path d="M12 0C7.58 0 4 3.58 4 8c0 5.25 8 16 8 16s8-10.75 8-16c0-4.42-3.58-8-8-8z"
              fill="#dc2626" stroke="white" stroke-width="1.5"/>
            <circle cx="12" cy="8" r="3.5" fill="white" opacity="0.95"/>
          </svg>
        </div>`,
      className: '',
      iconSize: [30, 40],
      iconAnchor: [15, 40]
    });
    if (markerRef.current) {
      markerRef.current.setLatLng([lat, lng]);
    } else {
      const marker = L.marker([lat, lng], {
        draggable: true,
        icon: pinIcon
      }).addTo(mapInstanceRef.current);
      marker.on('dragend', () => {
        const pos = marker.getLatLng();
        setFormData(prev => ({
          ...prev,
          lat: pos.lat,
          lng: pos.lng
        }));
      });
      markerRef.current = marker;
    }
  };
  // Update marker when GPS/search sets location
  useEffect(() => {
    if (mapDestroyedRef.current) return;
    if (!mapInstanceRef.current || !formData.lat || !formData.lng) return;
    updateMarker(formData.lat, formData.lng);
    mapInstanceRef.current.setView([formData.lat, formData.lng], 15);
  }, [formData.lat, formData.lng]);
  const openAddModal = () => {
    setEditingClient(null);
    setFormData({
      codigo: '',
      nombre: '',
      telefono: '',
      ciudad: '',
      limite: '',
      cedula: '',
      rnc: '',
      ruta: '',
      direccion: '',
      vendedorId: '',
      vendedorNombre: '',
      dependencia: '',
      zona: '',
      lat: null,
      lng: null
    });
    setAddressSearch('');
    setSearchResults([]);
    setShowResults(false);
    setActiveTab('info');
    setIsModalOpen(true);
  };
  const openEditModal = (client: Client) => {
    setEditingClient(client);
    setFormData({
      codigo: client.codigo || '',
      nombre: client.nombre,
      telefono: client.telefono || '',
      ciudad: client.ciudad || '',
      limite: client.limite ? String(client.limite) : '',
      cedula: client.cedula || '',
      rnc: client.rnc || '',
      ruta: client.ruta || '',
      direccion: client.direccion,
      vendedorId: client.vendedorId || '',
      vendedorNombre: client.vendedorNombre || '',
      dependencia: client.dependencia || '',
      zona: client.zona,
      lat: client.lat || null,
      lng: client.lng || null
    });
    setAddressSearch('');
    setSearchResults([]);
    setShowResults(false);
    setActiveTab('info');
    setIsModalOpen(true);
  };
  const closeModal = () => {
    setIsModalOpen(false);
    setEditingClient(null);
    if (mapInstanceRef.current) {
      mapInstanceRef.current.remove();
      mapInstanceRef.current = null;
      markerRef.current = null;
    }
  };
  const handleUseCurrentLocation = async () => {
    setLocationError('');
    setIsGettingLocation(true);
    try {
      await new Promise<void>((resolve, reject) => {
        if (!navigator.geolocation) {
          reject(new Error('GPS no disponible en este dispositivo'));
          return;
        }
        navigator.geolocation.getCurrentPosition(pos => {
          const lat = pos.coords.latitude;
          const lng = pos.coords.longitude;
          setFormData(prev => ({
            ...prev,
            lat,
            lng
          }));
          // Update user location marker on map
          if (mapInstanceRef.current && userLocationMarkerRef.current) {
            userLocationMarkerRef.current.setLatLng([lat, lng]);
            mapInstanceRef.current.setView([lat, lng], 17);
          } else if (mapInstanceRef.current) {
            mapInstanceRef.current.setView([lat, lng], 17);
          }
          resolve();
        }, err => {
          if (err.code === err.PERMISSION_DENIED) {
            reject(new Error('Permiso de ubicación denegado. Actívalo en la configuración del navegador.'));
          } else if (err.code === err.POSITION_UNAVAILABLE) {
            reject(new Error('Ubicación no disponible. Verifica que el GPS esté activado.'));
          } else {
            reject(new Error('No se pudo obtener la ubicación. Intenta nuevamente.'));
          }
        }, {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0
        });
      });
    } catch (err: any) {
      setLocationError(err.message);
    } finally {
      setIsGettingLocation(false);
    }
  };
  const handleSave = async () => {
    if (!formData.nombre || !formData.zona || !formData.direccion) return;
    setIsSaving(true);
    try {
      const selectedVendor = vendors.find(v => v.id === formData.vendedorId);
      const clientData = {
        codigo: formData.codigo,
        nombre: formData.nombre,
        telefono: formData.telefono,
        ciudad: formData.ciudad,
        limite: Number(formData.limite) || 0,
        cedula: formData.cedula,
        rnc: formData.rnc,
        ruta: formData.ruta,
        direccion: formData.direccion,
        vendedorId: formData.vendedorId || undefined,
        vendedorNombre: selectedVendor?.nombre || undefined,
        dependencia: formData.dependencia,
        zona: formData.zona,
        lat: formData.lat || undefined,
        lng: formData.lng || undefined
      };
      // We cast to any here because the addClient/updateClient types might not be fully updated in the context definition yet
      // but the underlying implementation handles the new fields if the migration ran.
      if (editingClient) {
        await updateClient(editingClient.id, clientData as any);
        addActivityLog('Editó farmacia', `Farmacia: ${formData.nombre} | Código: ${formData.codigo}`);
      } else {
        await addClient(clientData as any);
        addActivityLog('Creó farmacia', `Farmacia: ${formData.nombre} | Código: ${formData.codigo}`);
      }
      closeModal();
    } finally {
      setIsSaving(false);
    }
  };
  const handleDelete = async () => {
    if (!editingClient) return;
    setIsSaving(true);
    try {
      await deleteClient(editingClient.id);
      addActivityLog('Eliminó farmacia', `Farmacia: ${editingClient.nombre}`);
      setIsDeleteModalOpen(false);
      closeModal();
    } finally {
      setIsSaving(false);
    }
  };
  const handleAddressSearch = (query: string) => {
    setAddressSearch(query);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    if (query.length < 2) {
      setSearchResults([]);
      setShowResults(false);
      return;
    }
    setIsSearching(true);
    searchTimeoutRef.current = setTimeout(async () => {
      try {
        // Try Google Places first if API key is available
        if (hasGoogleApiKey()) {
          const googleResults = await searchGooglePlaces(query);
          if (googleResults.length > 0) {
            setSearchResults(googleResults);
            setShowResults(true);
            setIsSearching(false);
            return;
          }
        }
        // Fallback to free geocoders
        const searchQuery = encodeURIComponent(query);
        const [photonRes, nominatimRes, nominatimPOI] = await Promise.all([fetch(`https://photon.komoot.io/api/?q=${searchQuery}&limit=8&lang=es&lat=18.48&lon=-69.93`).then(r => r.json()).catch(() => ({
          features: []
        })), fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${searchQuery}&limit=6&addressdetails=1&accept-language=es`, {
          headers: {
            'Accept-Language': 'es'
          }
        }).then(r => r.json()).catch(() => []), query.toLowerCase().includes('farmacia') ? Promise.resolve([]) : fetch(`https://nominatim.openstreetmap.org/search?format=json&q=farmacia+${searchQuery}&limit=4&countrycodes=do&addressdetails=1&accept-language=es`, {
          headers: {
            'Accept-Language': 'es'
          }
        }).then(r => r.json()).catch(() => [])]);
        const seen = new Set<string>();
        const merged: Array<{
          display_name: string;
          lat: string;
          lon: string;
        }> = [];
        if (photonRes.features) {
          for (const f of photonRes.features) {
            const lat = String(f.geometry.coordinates[1]);
            const lon = String(f.geometry.coordinates[0]);
            const key = `${parseFloat(lat).toFixed(4)},${parseFloat(lon).toFixed(4)}`;
            if (!seen.has(key)) {
              seen.add(key);
              const props = f.properties || {};
              const parts = [props.name, props.street, props.city || props.district, props.state, props.country].filter(Boolean);
              merged.push({
                display_name: parts.join(', '),
                lat,
                lon
              });
            }
          }
        }
        for (const result of [...nominatimRes, ...nominatimPOI]) {
          const key = `${parseFloat(result.lat).toFixed(4)},${parseFloat(result.lon).toFixed(4)}`;
          if (!seen.has(key)) {
            seen.add(key);
            merged.push(result);
          }
        }
        setSearchResults(merged.slice(0, 12));
        setShowResults(true);
      } catch {
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 350);
  };
  const formatDisplayName = (display_name: string) => {
    return display_name.replace(/, República Dominicana$/, '').replace(/, Dominican Republic$/, '');
  };
  const handleSelectAddress = (result: {
    display_name: string;
    lat: string;
    lon: string;
  }) => {
    const lat = parseFloat(result.lat);
    const lng = parseFloat(result.lon);
    setFormData(prev => ({
      ...prev,
      lat,
      lng
    }));
    setAddressSearch(result.display_name);
    setShowResults(false);
  };
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-DO', {
      style: 'currency',
      currency: 'DOP',
      minimumFractionDigits: 0
    }).format(amount);
  };
  return <div className="space-y-6" data-id="element-478">
      <PageHeader title="Farmacias" subtitle="Gestión de farmacias y ubicaciones" icon={<BuildingIcon className="w-6 h-6" data-id="element-480" />} data-id="element-479">
        <div className="flex items-center gap-3 w-full sm:w-auto" data-id="element-481">
          <div className="relative flex-1 sm:w-64" data-id="element-482">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" data-id="element-483" />
            <input type="text" placeholder="Buscar por nombre, código o zona..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-9 pr-4 py-2.5 text-sm bg-white border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-[#2952C4]/20 focus:border-[#2952C4] shadow-sm" data-id="element-484" />
          </div>
          {isAdmin && <Button onClick={openAddModal} leftIcon={<PlusIcon className="w-4 h-4" data-id="element-486" />} data-id="element-485">
              Nueva Farmacia
            </Button>}
        </div>
      </PageHeader>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 animate-stagger-1" data-id="element-487">
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5" data-id="element-488">
          <div className="flex items-center gap-3" data-id="element-489">
            <div className="p-2.5 bg-blue-50 rounded-xl" data-id="element-490">
              <BuildingIcon className="w-5 h-5 text-blue-600" data-id="element-491" />
            </div>
            <div data-id="element-492">
              <p className="text-2xl font-bold text-gray-900" data-id="element-493">
                {clients.length}
              </p>
              <p className="text-xs text-gray-500 font-medium" data-id="element-494">
                Total Farmacias
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5" data-id="element-495">
          <div className="flex items-center gap-3" data-id="element-496">
            <div className="p-2.5 bg-emerald-50 rounded-xl" data-id="element-497">
              <MapPinIcon className="w-5 h-5 text-emerald-600" data-id="element-498" />
            </div>
            <div data-id="element-499">
              <p className="text-2xl font-bold text-emerald-600" data-id="element-500">
                {clients.filter(c => c.lat && c.lng).length}
              </p>
              <p className="text-xs text-gray-500 font-medium" data-id="element-501">
                Con Ubicación GPS
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5" data-id="element-502">
          <div className="flex items-center gap-3" data-id="element-503">
            <div className="p-2.5 bg-orange-50 rounded-xl" data-id="element-504">
              <XCircleIcon className="w-5 h-5 text-orange-600" data-id="element-505" />
            </div>
            <div data-id="element-506">
              <p className="text-2xl font-bold text-orange-600" data-id="element-507">
                {clients.filter(c => !c.lat || !c.lng).length}
              </p>
              <p className="text-xs text-gray-500 font-medium" data-id="element-508">Sin Ubicación</p>
            </div>
          </div>
        </div>
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-5" data-id="element-509">
          <div className="flex items-center gap-3" data-id="element-510">
            <div className="p-2.5 bg-purple-50 rounded-xl" data-id="element-511">
              <MapIcon className="w-5 h-5 text-purple-600" data-id="element-512" />
            </div>
            <div data-id="element-513">
              <p className="text-2xl font-bold text-purple-600" data-id="element-514">
                {new Set(clients.map(c => c.zona)).size}
              </p>
              <p className="text-xs text-gray-500 font-medium" data-id="element-515">Zonas</p>
            </div>
          </div>
        </div>
      </div>

      {/* Clients TABLE */}
      <div className="animate-stagger-2" data-id="element-516">
        {filteredClients.length === 0 ? <div className="bg-white rounded-2xl border border-slate-200/60 shadow-[0_1px_3px_rgba(0,0,0,0.04)] p-5 text-center animate-fade-in" data-id="element-517">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-100 rounded-2xl mb-4" data-id="element-518">
              <BuildingIcon className="w-8 h-8 text-gray-400" data-id="element-519" />
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-1" data-id="element-520">
              {searchQuery ? 'No se encontraron farmacias' : 'No hay farmacias registradas'}
            </h3>
            <p className="text-gray-500 text-sm" data-id="element-521">
              {searchQuery ? 'Intenta con otro término de búsqueda' : 'Agrega tu primera farmacia para comenzar'}
            </p>
          </div> : <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden" data-id="element-522">
            <div className="overflow-x-auto" data-id="element-523">
              <table className="w-full" data-id="element-524">
                <thead data-id="element-525">
                  <tr className="bg-gray-50/80 border-b border-gray-100" data-id="element-526">
                    <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-527">
                      Código
                    </th>
                    <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-528">
                      Nombre
                    </th>
                    <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-529">
                      Teléfono
                    </th>
                    <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-530">
                      Ciudad
                    </th>
                    <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-531">
                      Límite
                    </th>
                    <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-532">
                      Cédula
                    </th>
                    <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-533">
                      RNC
                    </th>
                    <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-534">
                      Ruta
                    </th>
                    <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-535">
                      Dirección
                    </th>
                    <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-536">
                      Vendedor
                    </th>
                    <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-537">
                      Dependencia
                    </th>
                    <th className="px-4 py-3.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-538">
                      Ruta Zona
                    </th>
                    <th className="px-4 py-3.5 text-center text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap" data-id="element-539">
                      GPS
                    </th>
                    {isAdmin && <th className="px-4 py-3.5 w-16" data-id="element-540"></th>}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100" data-id="element-541">
                  {paginatedClients.map(client => <tr key={client.id} className="hover:bg-gray-50/50 transition-colors" data-id="element-542">
                      <td className="px-4 py-3.5 text-sm text-gray-600 font-mono whitespace-nowrap" data-id="element-543">
                        {client.codigo || '—'}
                      </td>
                      <td className="px-4 py-3.5" data-id="element-544">
                        <div className="flex items-center gap-3 min-w-[200px]" data-id="element-545">
                          <div className="p-2 bg-blue-50 rounded-lg flex-shrink-0" data-id="element-546">
                            <BuildingIcon className="w-4 h-4 text-blue-600" data-id="element-547" />
                          </div>
                          <span className="font-semibold text-gray-900 text-sm truncate" title={client.nombre} data-id="element-548">
                            {client.nombre}
                          </span>
                        </div>
                      </td>
                      <td className="px-4 py-3.5 text-sm text-gray-600 whitespace-nowrap" data-id="element-549">
                        {client.telefono || '—'}
                      </td>
                      <td className="px-4 py-3.5 text-sm text-gray-600 whitespace-nowrap" data-id="element-550">
                        {client.ciudad || '—'}
                      </td>
                      <td className="px-4 py-3.5 text-sm text-gray-900 font-medium whitespace-nowrap" data-id="element-551">
                        {client.limite ? formatCurrency(client.limite) : '—'}
                      </td>
                      <td className="px-4 py-3.5 text-sm text-gray-600 whitespace-nowrap" data-id="element-552">
                        {client.cedula || '—'}
                      </td>
                      <td className="px-4 py-3.5 text-sm text-gray-600 whitespace-nowrap" data-id="element-553">
                        {client.rnc || '—'}
                      </td>
                      <td className="px-4 py-3.5 text-sm text-gray-600 whitespace-nowrap" data-id="element-554">
                        {client.ruta || '—'}
                      </td>
                      <td className="px-4 py-3.5 text-sm text-gray-600 max-w-[200px] truncate" title={client.direccion} data-id="element-555">
                        {client.direccion}
                      </td>
                      <td className="px-4 py-3.5 whitespace-nowrap" data-id="element-556">
                        {client.vendedorNombre ? <div className="flex items-center gap-1.5 text-sm text-gray-700" data-id="element-557">
                            <UserIcon className="w-3.5 h-3.5 text-blue-400" data-id="element-558" />
                            <span className="font-medium" data-id="element-559">
                              {client.vendedorNombre}
                            </span>
                          </div> : <span className="text-xs text-gray-400 italic" data-id="element-560">
                            Sin asignar
                          </span>}
                      </td>
                      <td className="px-4 py-3.5 text-sm text-gray-600 whitespace-nowrap" data-id="element-561">
                        {client.dependencia || '—'}
                      </td>
                      <td className="px-4 py-3.5 whitespace-nowrap" data-id="element-562">
                        <Badge variant="info" size="sm" data-id="element-563">
                          {client.zona}
                        </Badge>
                      </td>
                      <td className="px-4 py-3.5 text-center whitespace-nowrap" data-id="element-564">
                        {client.lat && client.lng ? <div className="inline-flex items-center gap-2" data-id="element-565">
                            <div className="inline-flex items-center gap-1.5" data-id="element-566">
                              <div className="w-2 h-2 rounded-full bg-emerald-500" data-id="element-567" />
                              <span className="text-xs text-emerald-600 font-medium" data-id="element-568">
                                Sí
                              </span>
                            </div>
                            <a href={`https://www.google.com/maps?q=${client.lat},${client.lng}`} target="_blank" rel="noopener noreferrer" className="text-[10px] font-semibold text-blue-500 hover:text-blue-700 bg-blue-50 px-1.5 py-0.5 rounded" title="Ver en Google Maps" data-id="element-569">
                              📍 Maps
                            </a>
                            <a href={`https://www.google.com/maps/dir/?api=1&destination=${client.lat},${client.lng}`} target="_blank" rel="noopener noreferrer" className="text-[10px] font-semibold text-green-600 hover:text-green-800 bg-green-50 px-1.5 py-0.5 rounded" title="Cómo llegar" data-id="element-570">
                              🧭 Ir
                            </a>
                          </div> : <div className="inline-flex items-center gap-1.5" data-id="element-571">
                            <div className="w-2 h-2 rounded-full bg-gray-300" data-id="element-572" />
                            <span className="text-xs text-gray-400" data-id="element-573">No</span>
                          </div>}
                      </td>
                      {isAdmin && <td className="px-4 py-3.5" data-id="element-574">
                          <Button variant="ghost" size="sm" onClick={() => openEditModal(client)} data-id="element-575">
                            <EditIcon className="w-4 h-4 text-gray-400" data-id="element-576" />
                          </Button>
                        </td>}
                    </tr>)}
                </tbody>
              </table>
            </div>
            <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} totalItems={filteredClients.length} itemsPerPage={ITEMS_PER_PAGE} data-id="element-577" />
          </div>}
      </div>

      {/* Add/Edit Modal */}
      <Modal isOpen={isModalOpen} onClose={closeModal} title={editingClient ? 'Editar Farmacia' : 'Nueva Farmacia'} size="lg" data-id="element-578">
        <ModalBody data-id="element-579">
          <div className="flex gap-1 p-1 bg-gray-100 rounded-xl mb-6" data-id="element-580">
            <button onClick={() => setActiveTab('info')} className={`flex-1 py-2 px-4 text-sm font-medium rounded-lg transition-colors ${activeTab === 'info' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-581">
              Información
            </button>
            <button onClick={() => setActiveTab('location')} className={`flex-1 py-2 px-4 text-sm font-medium rounded-lg transition-colors ${activeTab === 'location' ? 'bg-white text-gray-900 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-582">
              Ubicación GPS
            </button>
          </div>

          {activeTab === 'info' && <div className="space-y-4" data-id="element-583">
              <div className="grid grid-cols-2 gap-4" data-id="element-584">
                <Input label="Código" value={formData.codigo} onChange={e => setFormData({
              ...formData,
              codigo: e.target.value
            })} placeholder="Ej: CLI-001" data-id="element-585" />
                <Input label="Nombre de la Farmacia" value={formData.nombre} onChange={e => setFormData({
              ...formData,
              nombre: e.target.value
            })} placeholder="Ej: Farmacia Central" data-id="element-586" />
              </div>

              <div className="grid grid-cols-2 gap-4" data-id="element-587">
                <Input label="RNC" value={formData.rnc} onChange={e => setFormData({
              ...formData,
              rnc: e.target.value
            })} placeholder="Ej: 101-12345-6" data-id="element-588" />
                <Input label="Cédula" value={formData.cedula} onChange={e => setFormData({
              ...formData,
              cedula: e.target.value
            })} placeholder="Ej: 001-1234567-8" data-id="element-589" />
              </div>

              <div className="grid grid-cols-2 gap-4" data-id="element-590">
                <Input label="Teléfono" value={formData.telefono} onChange={e => setFormData({
              ...formData,
              telefono: e.target.value
            })} placeholder="809-555-1234" data-id="element-591" />
                <Input label="Ciudad" value={formData.ciudad} onChange={e => setFormData({
              ...formData,
              ciudad: e.target.value
            })} placeholder="Ej: Santo Domingo" data-id="element-592" />
              </div>

              <Input label="Dirección" value={formData.direccion} onChange={e => setFormData({
            ...formData,
            direccion: e.target.value
          })} placeholder="Ej: Calle El Conde #123" data-id="element-593" />

              <div className="grid grid-cols-2 gap-4" data-id="element-594">
                <Input label="Ruta" value={formData.ruta} onChange={e => setFormData({
              ...formData,
              ruta: e.target.value
            })} placeholder="Ej: Ruta 1" data-id="element-595" />
                <Input label="Ruta Zona" value={formData.zona} onChange={e => setFormData({
              ...formData,
              zona: e.target.value
            })} placeholder="Ej: Zona Colonial" data-id="element-596" />
              </div>

              <div className="grid grid-cols-2 gap-4" data-id="element-597">
                <Input label="Límite de Crédito (DOP)" type="number" value={formData.limite} onChange={e => setFormData({
              ...formData,
              limite: e.target.value
            })} placeholder="0.00" data-id="element-598" />
                <Input label="Dependencia" value={formData.dependencia} onChange={e => setFormData({
              ...formData,
              dependencia: e.target.value
            })} placeholder="Ej: Casa Matriz" data-id="element-599" />
              </div>

              {/* Vendedor asignado */}
              <div data-id="element-600">
                <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-601">
                  Vendedor Asignado
                </label>
                <select value={formData.vendedorId} onChange={e => {
              const v = vendors.find(v => v.id === e.target.value);
              setFormData({
                ...formData,
                vendedorId: e.target.value,
                vendedorNombre: v?.nombre || ''
              });
            }} className="block w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-602">
                  {vendorOptions.map(opt => <option key={opt.value} value={opt.value} data-id="element-603">
                      {opt.label}
                    </option>)}
                </select>
              </div>
            </div>}

          {activeTab === 'location' && <div className="space-y-4" data-id="element-604">
              <div className="p-3 bg-blue-50 rounded-xl border border-blue-200" data-id="element-605">
                <p className="text-sm text-blue-700" data-id="element-606">
                  <strong data-id="element-607">Instrucciones:</strong> Busca una dirección, haz clic
                  en el mapa, o usa el botón de ubicación actual.
                  {hasGoogleApiKey() ? <span className="ml-1 inline-flex items-center gap-1 px-2 py-0.5 bg-white rounded-lg text-xs font-semibold text-emerald-700 border border-emerald-200" data-id="element-608">
                      ✓ Google Maps Places activo
                    </span> : <span className="ml-1 inline-flex items-center gap-1 px-2 py-0.5 bg-white rounded-lg text-xs font-semibold text-blue-700 border border-blue-200" data-id="element-609">
                      🔍 Búsqueda con Photon + OpenStreetMap
                    </span>}
                </p>
              </div>

              <div className="flex gap-2" data-id="element-610">
                <div className="relative flex-1" data-id="element-611">
                  <div className="relative" data-id="element-612">
                    <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" data-id="element-613" />
                    <input type="text" placeholder="Buscar en República Dominicana..." value={addressSearch} onChange={e => handleAddressSearch(e.target.value)} onFocus={() => searchResults.length > 0 && setShowResults(true)} onBlur={() => setTimeout(() => setShowResults(false), 200)} className="w-full pl-9 pr-4 py-2.5 text-sm border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500" data-id="element-614" />
                    {isSearching && <div className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" data-id="element-615" />}
                  </div>
                  {showResults && searchResults.length > 0 && <div className="absolute z-[9999] top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-2xl overflow-hidden" data-id="element-616">
                      {searchResults.map((result, i) => <button key={i} onMouseDown={() => handleSelectAddress(result)} className="w-full text-left px-4 py-3 text-sm text-gray-700 hover:bg-blue-50 border-b border-gray-100 last:border-0 transition-colors" data-id="element-617">
                          <div className="flex items-start gap-2" data-id="element-618">
                            <MapPinIcon className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" data-id="element-619" />
                            <span className="line-clamp-2" data-id="element-620">
                              {formatDisplayName(result.display_name)}
                            </span>
                          </div>
                        </button>)}
                      <a href={`https://www.google.com/maps/search/${encodeURIComponent(addressSearch)}`} target="_blank" rel="noopener noreferrer" onMouseDown={e => e.preventDefault()} className="w-full flex items-center gap-2 px-4 py-3 text-sm font-semibold text-[#4285f4] hover:bg-blue-50 border-t border-gray-200 transition-colors" data-id="element-621">
                        <svg className="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24" fill="none" data-id="element-622">
                          <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" fill="#EA4335" data-id="element-623" />
                          <circle cx="12" cy="9" r="2.5" fill="white" data-id="element-624" />
                        </svg>
                        Buscar "{addressSearch}" en Google Maps
                        <svg className="w-3.5 h-3.5 ml-auto flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" data-id="element-625">
                          <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6M15 3h6v6M10 14L21 3" data-id="element-626" />
                        </svg>
                      </a>
                    </div>}
                  {showResults && searchResults.length === 0 && !isSearching && addressSearch.length >= 2 && <div className="absolute z-[9999] top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-xl shadow-2xl overflow-hidden" data-id="element-627">
                        <div className="p-4 text-sm text-gray-500 text-center" data-id="element-628">
                          No se encontraron resultados
                        </div>
                        <a href={`https://www.google.com/maps/search/${encodeURIComponent(addressSearch)}`} target="_blank" rel="noopener noreferrer" onMouseDown={e => e.preventDefault()} className="w-full flex items-center gap-2 px-4 py-3 text-sm font-semibold text-[#4285f4] hover:bg-blue-50 border-t border-gray-200 transition-colors" data-id="element-629">
                          <svg className="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24" fill="none" data-id="element-630">
                            <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" fill="#EA4335" data-id="element-631" />
                            <circle cx="12" cy="9" r="2.5" fill="white" data-id="element-632" />
                          </svg>
                          Buscar "{addressSearch}" en Google Maps
                          <svg className="w-3.5 h-3.5 ml-auto flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" data-id="element-633">
                            <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6M15 3h6v6M10 14L21 3" data-id="element-634" />
                          </svg>
                        </a>
                      </div>}
                </div>
                <Button variant="secondary" onClick={handleUseCurrentLocation} isLoading={isGettingLocation} leftIcon={<NavigationIcon className="w-4 h-4" data-id="element-636" />} data-id="element-635">
                  Mi ubicación
                </Button>
              </div>

              {locationError && <div className="flex items-center gap-2 p-3 bg-rose-50 border border-rose-200 rounded-xl text-sm text-rose-700" data-id="element-637">
                  <span data-id="element-638">⚠️</span>
                  <span data-id="element-639">{locationError}</span>
                </div>}

              <div className="grid grid-cols-2 gap-3" data-id="element-640">
                <div data-id="element-641">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-642">
                    Latitud
                  </label>
                  <div className="block w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm text-gray-700 font-mono" data-id="element-643">
                    {formData.lat ? formData.lat.toFixed(6) : <span className="text-gray-400" data-id="element-644">—</span>}
                  </div>
                </div>
                <div data-id="element-645">
                  <label className="block text-sm font-medium text-gray-700 mb-1.5" data-id="element-646">
                    Longitud
                  </label>
                  <div className="block w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm text-gray-700 font-mono" data-id="element-647">
                    {formData.lng ? formData.lng.toFixed(6) : <span className="text-gray-400" data-id="element-648">—</span>}
                  </div>
                </div>
              </div>

              <div className="h-[300px] w-full rounded-xl border border-gray-200 overflow-hidden relative z-0" data-id="element-649">
                <LeafletMap onMapReady={handleMapReady} center={formData.lat && formData.lng ? [formData.lat, formData.lng] : undefined} data-id="element-650" />
              </div>

              {formData.lat && formData.lng && <div className="space-y-3" data-id="element-651">
                  <div className="flex items-center gap-3 p-3 bg-emerald-50 rounded-xl border border-emerald-200" data-id="element-652">
                    <CheckCircleIcon className="w-5 h-5 text-emerald-600 flex-shrink-0" data-id="element-653" />
                    <div className="flex-1" data-id="element-654">
                      <p className="text-sm font-medium text-emerald-800" data-id="element-655">
                        Ubicación seleccionada
                      </p>
                      <p className="text-xs text-emerald-600 font-mono" data-id="element-656">
                        {formData.lat.toFixed(6)}, {formData.lng.toFixed(6)}
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-2" data-id="element-657">
                    <a href={`https://www.google.com/maps?q=${formData.lat},${formData.lng}`} target="_blank" rel="noopener noreferrer" className="flex-1 flex items-center justify-center gap-2 px-3 py-2.5 bg-[#4285f4] hover:bg-[#3367d6] text-white rounded-xl text-sm font-semibold transition-colors" data-id="element-658">
                      📍 Ver en Google Maps
                    </a>
                    <a href={`https://www.google.com/maps/dir/?api=1&destination=${formData.lat},${formData.lng}`} target="_blank" rel="noopener noreferrer" className="flex-1 flex items-center justify-center gap-2 px-3 py-2.5 bg-[#34a853] hover:bg-[#2d9249] text-white rounded-xl text-sm font-semibold transition-colors" data-id="element-659">
                      🧭 Cómo llegar
                    </a>
                  </div>
                </div>}
            </div>}
        </ModalBody>
        <ModalFooter data-id="element-660">
          <div className="flex items-center justify-between w-full" data-id="element-661">
            {editingClient && isAdmin ? <Button variant="danger" onClick={() => setIsDeleteModalOpen(true)} leftIcon={<TrashIcon className="w-4 h-4" data-id="element-663" />} data-id="element-662">
                Eliminar
              </Button> : <div data-id="element-664" />}
            <div className="flex items-center gap-3" data-id="element-665">
              <Button variant="secondary" onClick={closeModal} data-id="element-666">
                Cancelar
              </Button>
              <Button onClick={handleSave} isLoading={isSaving} disabled={!formData.nombre || !formData.zona || !formData.direccion} data-id="element-667">
                {editingClient ? 'Guardar Cambios' : 'Crear Farmacia'}
              </Button>
            </div>
          </div>
        </ModalFooter>
      </Modal>

      <Modal isOpen={isDeleteModalOpen} onClose={() => setIsDeleteModalOpen(false)} title="Eliminar Farmacia" size="sm" data-id="element-668">
        <ModalBody data-id="element-669">
          <p className="text-gray-600" data-id="element-670">
            ¿Estás seguro de que deseas eliminar{' '}
            <strong data-id="element-671">{editingClient?.nombre}</strong>? Esta acción no se puede
            deshacer.
          </p>
        </ModalBody>
        <ModalFooter data-id="element-672">
          <Button variant="secondary" onClick={() => setIsDeleteModalOpen(false)} data-id="element-673">
            Cancelar
          </Button>
          <Button variant="danger" onClick={handleDelete} isLoading={isSaving} data-id="element-674">
            Eliminar
          </Button>
        </ModalFooter>
      </Modal>
    </div>;
}